/**
 * Registrations API Module
 *
 * API calls for course registration (Teilnehmer) management
 */

import { apiClient } from './client'
import type {
  CourseRegistrationDto,
  CourseRegistrationApiResponse,
  CreateCourseRegistrationDto,
  UpdateCourseRegistrationDto,
} from '@/types/api.types'

/**
 * Transform API response to normalized DTO
 */
function transformRegistration(raw: CourseRegistrationApiResponse): CourseRegistrationDto {
  const result: CourseRegistrationDto = {
    id: raw.id,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    courseId: raw.courseId,
    participantId: raw.personId,
    participantName:
      raw.person.fullName?.trim() || `${raw.person.firstName} ${raw.person.lastName}`.trim(),
    participantFirstName: raw.person.firstName?.trim() || '',
    participantLastName: raw.person.lastName?.trim() || '',
    registrationDate: raw.createdAt,
    isActive: true, // API doesn't return isActive, assume all returned registrations are active
    isProvisional: raw.isProvisional ?? false,
  }

  // Add optional fields only if they exist
  if (raw.person.email) {
    result.participantEmail = raw.person.email.trim()
  }
  if (raw.person.phoneNumber) {
    result.participantPhone = raw.person.phoneNumber.trim()
  }
  if (raw.person.dateOfBirth) {
    result.participantBirthDate = raw.person.dateOfBirth
  }
  if (raw.person.isHistorical) {
    result.isHistorical = true
    result.membershipEndedOn = raw.person.membershipEndedOn ?? null
  }

  return result
}

export const registrationsApi = {
  /**
   * Get all registrations for a course — einschließlich ausgetretener Mitglieder
   * (`isHistorical` + `membershipEndedOn`). Wer nur aktuelle Mitglieder will, filtert im
   * Store (`getActiveByCourseId`); die Anwesenheit eines Termins entscheidet nach dessen
   * Datum (`getForSessionDate`). Ein älteres Backend ignoriert den Parameter.
   */
  async getByCourse(courseId: number): Promise<CourseRegistrationDto[]> {
    const response = await apiClient.get<CourseRegistrationApiResponse[]>(
      `/CourseRegistrations/by-course/${courseId}`,
      { params: { includeFormer: true } }
    )
    return response.data.map(transformRegistration)
  },

  /**
   * Get single registration by ID
   */
  async getById(id: number): Promise<CourseRegistrationDto> {
    const response = await apiClient.get<CourseRegistrationApiResponse>(
      `/CourseRegistrations/${id}`
    )
    return transformRegistration(response.data)
  },

  /**
   * Create a new course registration.
   * Backend has upsert logic: restores soft-deleted, 409 on active duplicate.
   */
  async create(dto: CreateCourseRegistrationDto): Promise<CourseRegistrationDto> {
    const response = await apiClient.post<CourseRegistrationApiResponse>(
      '/CourseRegistrations',
      dto
    )
    return transformRegistration(response.data)
  },

  /**
   * Update a course registration (e.g. promote provisional → regular).
   */
  async update(
    courseId: number,
    participantId: number,
    dto: UpdateCourseRegistrationDto
  ): Promise<void> {
    await apiClient.put(`/CourseRegistrations/${courseId}/${participantId}`, dto)
  },

  /**
   * Remove a course registration (soft-delete). Attendances are NOT affected.
   */
  async remove(courseId: number, participantId: number): Promise<void> {
    await apiClient.delete(`/CourseRegistrations/${courseId}/${participantId}`)
  },
}
